import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useEffectiveIdentity } from './useEffectiveIdentity';

// Generate a unique session ID
const generateSessionId = () => {
  const stored = sessionStorage.getItem('activity_session_id');
  if (stored) return stored;
  
  const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('activity_session_id', newId);
  return newId;
};

// Get browser info
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  
  return browser;
};

// Get device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
};

interface ActivityLog {
  action_type: string;
  action_category: string;
  entity_type?: string;
  entity_id?: string;
  entity_name?: string;
  details?: any;
  old_value?: any;
  new_value?: any;
}

export const useActivityTracker = () => {
  const { user } = useAuth();
  const { effectiveRole: role, effectiveDepartment: department, isAdmin, loading: roleLoading } = useEffectiveIdentity();
  const location = useLocation();
  const sessionId = useRef(generateSessionId());
  const lastPageRef = useRef<string>('');
  const pageStartTime = useRef<number>(Date.now());
  // Guards to prevent duplicate logs (React StrictMode, rapid navigations)
  const loginLoggedRef = useRef<boolean>(false);
  const lastLoggedPathRef = useRef<string>('');
  const lastLoggedAtRef = useRef<number>(0);
  const isInitializedRef = useRef<boolean>(false);

  // Track all users once role is loaded
  const shouldTrack = user && !roleLoading;

  // Track page views - ONLY when pathname actually changes
  useEffect(() => {
    if (!shouldTrack) {
      // Reset initialization if user becomes non-trackable
      if (isInitializedRef.current) {
        isInitializedRef.current = false;
      }
      return;
    }

    const trackPageView = async () => {
      const currentPath = location.pathname;
      const now = Date.now();

      // Skip tracking internal/technical routes
      const internalRoutes = ['/role-redirect', '/auth'];
      if (internalRoutes.includes(currentPath)) {
        console.log('[ActivityTracker] Skipping internal route:', currentPath);
        // Update refs but don't log
        lastPageRef.current = currentPath;
        lastLoggedPathRef.current = currentPath;
        lastLoggedAtRef.current = now;
        pageStartTime.current = now;
        if (!isInitializedRef.current) {
          isInitializedRef.current = true;
        }
        return;
      }

      // On first initialization, just set the refs without logging
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        lastPageRef.current = currentPath;
        lastLoggedPathRef.current = currentPath;
        lastLoggedAtRef.current = now;
        pageStartTime.current = now;
        console.log('[ActivityTracker] Initialized for path:', currentPath);
        return;
      }

      // De-dup guard: if same path was just logged, skip
      if (lastLoggedPathRef.current === currentPath) {
        console.log('[ActivityTracker] Skipping duplicate for same path:', currentPath);
        return;
      }

      const previousPage = lastPageRef.current;
      const timeOnPreviousPage = previousPage ? now - pageStartTime.current : 0;

      console.log('[ActivityTracker] Tracking page change:', {
        from: previousPage,
        to: currentPath,
        timeSpent: timeOnPreviousPage
      });

      // Log time spent on previous page if exists (but not if it was an internal route)
      if (previousPage && previousPage !== currentPath && timeOnPreviousPage > 1000 && !internalRoutes.includes(previousPage)) {
        await logActivity({
          action_type: 'page_exit',
          action_category: 'navigation',
          details: {
            from_page: previousPage,
            time_spent_ms: timeOnPreviousPage,
            time_spent_formatted: formatDuration(timeOnPreviousPage)
          }
        });
      }

      // Log new page view
      await logActivity({
        action_type: 'page_view',
        action_category: 'navigation',
        details: {
          previous_page: previousPage || 'direct',
          referrer: document.referrer || 'direct'
        }
      });

      lastPageRef.current = currentPath;
      pageStartTime.current = now;
      lastLoggedPathRef.current = currentPath;
      lastLoggedAtRef.current = now;
    };

    trackPageView();
  }, [location.pathname, shouldTrack]);

  // Track user login - only once per session (guarded against StrictMode)
  useEffect(() => {
    if (!shouldTrack) return;
    if (loginLoggedRef.current) return;

    const loginTrackedKey = `login_tracked_${sessionId.current}`;
    const hasTrackedLogin = sessionStorage.getItem(loginTrackedKey);
    
    if (!hasTrackedLogin) {
      const trackLogin = async () => {
        await logActivity({
          action_type: 'user_login',
          action_category: 'authentication',
          details: {
            login_time: new Date().toISOString()
          }
        });
        sessionStorage.setItem(loginTrackedKey, 'true');
        loginLoggedRef.current = true;
      };

      trackLogin();
    } else {
      // If already tracked in this tab session, prevent any further logs
      loginLoggedRef.current = true;
    }
  }, [shouldTrack, user?.id]);

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const logActivity = async (activity: ActivityLog) => {
    if (!shouldTrack) return;

    // Additional validation: ensure user has required fields
    if (!user?.id || !user?.email) {
      console.warn('[ActivityTracker] Cannot log activity: user id or email missing', { 
        userId: user?.id,
        hasEmail: !!user?.email
      });
      return;
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = getBrowserInfo();
      const deviceType = getDeviceType();

      const activityData = {
        user_id: user.id,
        user_email: user.email,
        user_name: user.user_metadata?.full_name || user.email,
        user_role: role || 'unknown',
        user_department: department || null,
        action_type: activity.action_type,
        action_category: activity.action_category,
        entity_type: activity.entity_type || null,
        entity_id: activity.entity_id || null,
        entity_name: activity.entity_name || null,
        page_url: location.pathname,
        page_title: document.title,
        details: activity.details || {},
        old_value: activity.old_value || null,
        new_value: activity.new_value || null,
        session_id: sessionId.current,
        user_agent: navigator.userAgent,
        browser,
        device_type: deviceType,
        timezone
      };

      const { error } = await supabase.from('user_activity_log').insert(activityData);
      
      if (error) {
        console.error('[ActivityTracker] Insert error:', {
          error,
          activityType: activity.action_type,
          userId: user.id
        });
      }
    } catch (error) {
      console.error('[ActivityTracker] Error logging activity:', error);
    }
  };

  // Public method to log custom activities - memoized to prevent re-renders
  const trackAction = useCallback(async (activity: ActivityLog) => {
    await logActivity(activity);
  }, [shouldTrack, user, role, department, location.pathname]);

  return { trackAction };
};
